import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '../../../imports/muiImport';
import { CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import MobileProductCard from './MobileProductCard';
import { categoryUrl } from '../../../shared/extra';
import { getAllProducts } from '../../../imports/generalImport';

/* -------------------------------------------------------------------------- */
/*                               HELPERS                                      */
/* -------------------------------------------------------------------------- */

/**
 * Returns ONLY the first matched category ID (0th index)
 * If no category matches, returns null
 */
const findCategoryId = (selectedSlugs = [], categories = []) => {
  const matchedCategory = categories.find((item) =>
    selectedSlugs.includes(categoryUrl(item.title))
  );

  return matchedCategory?.id ?? null;
};

/* -------------------------------------------------------------------------- */
/*                          COMPONENT                                         */
/* -------------------------------------------------------------------------- */

function MobileProductSection() {
  const location = useLocation();
  const coreCategoryData = useSelector((state) => state.Category.allData);

  const [allProducts, setAllProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(true);

  /* ----------------------------- API CALL ----------------------------- */

  const fetchProducts = useCallback(async (categoryId = null) => {
    setProductLoading(true);

    let isMounted = true;

    try {
      const response = await getAllProducts({
        search: '',
        filterBy: 'title',
        selectedCategories: categoryId ? [categoryId] : [],
        pagination: {
          sortBy: 'id',
          descending: false,
          page: 1,
          rowsPerPage: 1000,
          rowsNumber: 0,
        },
      });

      if (isMounted && response?.status) {
        setAllProducts(response?.data?.data || []);
      }
    } catch (error) {
      if (isMounted) {
        console.error('Product fetch error:', error);
        setAllProducts([]);
      }
    } finally {
      if (isMounted) setProductLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  /* ----------------------------- EFFECT ----------------------------- */

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get('category');

    if (categoryParam && coreCategoryData?.length) {
      const selectedSlugs = categoryParam.split(' ');
      const categoryId = findCategoryId(selectedSlugs, coreCategoryData);

      fetchProducts(categoryId);
    } else {
      fetchProducts(null);
    }
  }, [location.search, coreCategoryData, fetchProducts]);

  /* ----------------------------- LOADING ----------------------------- */

  if (productLoading) {
    return (
      <Box
        sx={{
          minHeight: '30vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  /* ----------------------------- UI ----------------------------- */

  return (
    <Box
      sx={{
        pt: 1,
        mb: 2,
        px: 1,
        maxWidth: '100vw',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(4, 1fr)',
          },
          rowGap: 4,
          mb: 1,
        }}
      >
        {allProducts.map((product) => (
          <MobileProductCard key={product.id || product.slug} product={product} />
        ))}
      </Box>
    </Box>
  );
}

export default MobileProductSection;
